import { useEffect, useState } from "react";
import { saveScrollPosition, useScrollRestore } from "../useScrollRestore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GearSix as SettingsIcon, ArrowsClockwise, CheckCircle } from "@phosphor-icons/react";
import { useQuestionnaireAnswers, useQuestionnaireSchema } from "../questionnaireApi";
import { extractApiError } from "../lib/errors";
import { reportError } from "../lib/errorReporting";
import { useLinkTelegram, useProfile, useRedeemInvite, useIntervalsSyncNow, useLastIntervalsSync, useStravaStatus, useStravaSyncNow, useStravaDisconnect, type Profile as ProfileT } from "../profileApi";
import { useZones } from "../zonesApi";
import { usePmcSeries, useSeasonStats, useCareerPeaks } from "../athleteMetricsApi";
import { usePersonalRecords } from "../personalRecordsApi";
import { PmcMini } from "../components/today/PmcMini";
import { Skeleton } from "../components/Skeleton";
import { CareerPeaksCard } from "../components/CareerPeaksCard";
import { PRTiles } from "../components/settings/PRTiles";
import { useUnitsPreference, formatDistance, unitLabel } from "../lib/useUnitsPreference";
import { AvatarUpload } from "../components/AvatarUpload";
import { CompanionModeToggle } from "../components/indigo/CompanionModeToggle";
import { ProfileLinksGrid } from "../components/profile/ProfileLinksGrid";
import { MetricDetailDrawer } from "../components/indigo/MetricDetailDrawer";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";

function fmtAge(sec: number, t: ReturnType<typeof useTranslation<"profile">>["t"]): string {
  if (sec < 60) return t("sync.just_now");
  if (sec < 3600) return t("sync.min_ago", { count: Math.floor(sec / 60) });
  if (sec < 86400) return t("sync.h_ago", { count: Math.floor(sec / 3600) });
  return t("sync.d_ago", { count: Math.floor(sec / 86400) });
}

export function StatusPill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md ${
        ok
          ? "bg-ok/15 text-ok border border-ok/20"
          : "bg-bg-raised text-fg-muted border border-bg-border"
      }`}
    >
      {label}
    </span>
  );
}

export function Section({
  title,
  ok,
  hint,
  connectedLabel,
  disconnectedLabel,
  children,
}: {
  title: string;
  /** Чип ПОДКЛЮЧЕНО/НЕ ПОДКЛЮЧЕНО. `undefined` — секция без чипа:
   *  у «Языка», «Истории платежей» и «Опасной зоны» нет понятия
   *  подключения, и обязательный чип печатал там бессмыслицу (v212). */
  ok?: boolean;
  hint?: string;
  connectedLabel?: string;
  disconnectedLabel?: string;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation("profile");
  const pill = ok
    ? (connectedLabel ?? t("status_connected"))
    : (disconnectedLabel ?? t("status_not_connected"));
  return (
    <Card variant="indigo" padding={16} className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-sm font-semibold text-fg-primary">{title}</div>
        {ok !== undefined && (
          <div className="ml-auto">
            <StatusPill ok={ok} label={pill} />
          </div>
        )}
      </div>
      {hint && <div className="text-xs text-fg-secondary leading-relaxed">{hint}</div>}
      {children}
    </Card>
  );
}

// Канонические кнопки переехали в components/ui — re-export для
// существующих импортов (TwoFactorBlock, тесты).
import { PrimaryButton, SecondaryButton } from "../components/ui/Button";
export { PrimaryButton, SecondaryButton };

export function TelegramBlock({ profile }: { profile: ProfileT }) {
  const { t } = useTranslation("profile");
  const link = useLinkTelegram();
  const [deeplink, setDeeplink] = useState<string | null>(null);
  const linked = profile.telegram_id != null;

  const onClick = () => {
    link.mutate(undefined, {
      onSuccess: (data) => {
        setDeeplink(data.deeplink);
        window.open(data.deeplink, "_blank");
      },
    });
  };

  return (
    <Section
      title="Telegram"
      ok={linked}
      connectedLabel={t("status_connected")}
      disconnectedLabel={t("status_not_connected")}
      hint={
        linked
          ? `@${profile.telegram_username || profile.telegram_id}`
          : t("telegram.hint_unlinked")
      }
    >
      {!linked && (
        <>
          <PrimaryButton onClick={onClick} disabled={link.isPending}>
            {link.isPending ? "..." : t("telegram.btn_link")}
          </PrimaryButton>
          {deeplink && (
            <div className="text-xs text-fg-secondary leading-relaxed pt-1">
              <a href={deeplink} className="text-accent underline" target="_blank" rel="noreferrer">
                {t("telegram.btn_link")}
              </a>
            </div>
          )}
        </>
      )}
    </Section>
  );
}

export function InviteBlock({ profile }: { profile: ProfileT }) {
  const { t } = useTranslation("profile");
  const { t: tc } = useTranslation("common");
  const redeem = useRedeemInvite();
  const [code, setCode] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const linked = profile.has_coach === true;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setOk(null);
    redeem.mutate(code.trim(), {
      onSuccess: () => {
        setOk(t("telegram.success"));
        setCode("");
      },
      onError: (e: unknown) => {
        reportError(e, {
          area: "profile",
          level: "warning",
          extra: { has_code: code.length > 0 },
          tags: { action: "telegram_link" },
        });
        setErr(extractApiError(e, tc("error")));
      },
    });
  };

  // Phase K3 — coach explicitly announced an away window.
  const coachAway = !!profile.coach?.is_currently_away;
  const coachAwayUntil = profile.coach?.away_until ?? null;
  const coachAwayMessage = profile.coach?.away_message ?? null;

  return (
    <Section
      title={t("settings_coach")}
      ok={linked}
      hint={linked ? t("coach.hint_linked") : t("coach.hint_unlinked")}
    >
      {linked && coachAway && (
        <div className="mb-3 rounded-pill border border-warn/30 bg-warn/10 px-3 py-2.5 text-xs">
          <div className="font-semibold text-warn">
            {t("coach.away_badge", {
              date: coachAwayUntil,
              defaultValue: "Тренер недоступен до {{date}}",
            })}
          </div>
          {coachAwayMessage && (
            <div className="text-warn/90 mt-1 whitespace-pre-wrap">
              {coachAwayMessage}
            </div>
          )}
        </div>
      )}
      {!linked && (
        <div className="mb-3">
          <a href="/marketplace" className="text-xs text-accent hover:underline">
            {t("coach.find_catalog")}
          </a>
        </div>
      )}
      {!linked && (
        <form onSubmit={onSubmit} className="space-y-2">
          <Input
            bare
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ABC123XYZ"
            className="w-full px-3.5 py-2.5 rounded-pill bg-bg-base border border-bg-border focus:border-accent focus:shadow-focus outline-none text-fg-primary placeholder:text-fg-dim text-sm uppercase tracking-wider transition-shadow"
            autoCapitalize="characters"
          />
          {err && (
            <div className="text-xs text-bad bg-bad-subtle/40 border border-bad/30 rounded-pill px-3 py-2">
              {err}
            </div>
          )}
          {ok && (
            <div className="text-xs text-ok bg-ok/10 border border-ok/20 rounded-pill px-3 py-2">
              {ok}
            </div>
          )}
          <PrimaryButton type="submit" disabled={redeem.isPending || !code.trim()}>
            {redeem.isPending ? "..." : t("coach.btn_activate")}
          </PrimaryButton>
        </form>
      )}
    </Section>
  );
}

export function IntervalsBlock({ profile }: { profile: ProfileT }) {
  const { t } = useTranslation("profile");
  const { t: tc } = useTranslation("common");
  const linked = !!profile.intervals_athlete_id;
  const available = profile.intervals_available;
  const syncNow = useIntervalsSyncNow();
  const { data: lastSync } = useLastIntervalsSync(linked);

  const onConnect = () => {
    window.location.href = `${import.meta.env.VITE_API_BASE || ""}/athlete/profile/intervals/start`;
  };

  const ageText = lastSync?.age_seconds != null
    ? fmtAge(lastSync.age_seconds, t)
    : null;

  return (
    <Section
      title="intervals.icu"
      ok={linked}
      hint={
        linked
          ? `athlete: ${profile.intervals_athlete_id}`
          : available
            ? t("intervals.hint_unlinked")
            : t("intervals.coming_soon")
      }
    >
      {!linked && available && <PrimaryButton onClick={onConnect}>{t("intervals.btn_connect")}</PrimaryButton>}
      {!linked && !available && <SecondaryButton disabled>{t("intervals.btn_soon")}</SecondaryButton>}
      {linked && (
        <div className="space-y-2">
          <SecondaryButton
            onClick={() => syncNow.mutate()}
            disabled={syncNow.isPending}
            className="flex items-center justify-center gap-2"
          >
            <ArrowsClockwise className={`w-4 h-4 ${syncNow.isPending ? "animate-spin" : ""}`} />
            {syncNow.isPending ? t("sync.syncing") : t("sync.btn_sync")}
          </SecondaryButton>
          {ageText && !syncNow.isPending && (
            <div className="text-[11px] text-fg-muted text-center">
              {t("sync.last_sync", { time: ageText })}
            </div>
          )}
          {syncNow.isSuccess && syncNow.data?.ok && (
            <div className="text-[11px] text-ok text-center flex items-center justify-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {t("sync.done")}
              {syncNow.data.stats && (
                <span className="text-fg-muted">
                  ·{" "}
                  {[
                    (syncNow.data.stats.activities_matched || 0) + (syncNow.data.stats.activities_unplanned || 0)
                      ? t("sync.workouts", { count: (syncNow.data.stats.activities_matched || 0) + (syncNow.data.stats.activities_unplanned || 0) })
                      : null,
                    (syncNow.data.stats.wellness_created || 0) + (syncNow.data.stats.wellness_updated || 0)
                      ? `${(syncNow.data.stats.wellness_created || 0) + (syncNow.data.stats.wellness_updated || 0)} wellness`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(", ") || t("sync.no_changes")}
                </span>
              )}
            </div>
          )}
          {syncNow.isError && (
            <div className="text-[11px] text-bad text-center">{t("sync.sync_error")}</div>
          )}
          {syncNow.isSuccess && !syncNow.data?.ok && (
            <div className="text-[11px] text-bad text-center">
              {syncNow.data?.error || tc("error")}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

export function StravaBlock({ profile }: { profile: ProfileT }) {
  const { t } = useTranslation("profile");
  const { t: tc } = useTranslation("common");
  const linked = !!profile.has_strava_token;
  const available = !!profile.strava_available;
  const { data: status } = useStravaStatus(linked);
  const syncNow = useStravaSyncNow();
  const disconnect = useStravaDisconnect();

  const onConnect = () => {
    window.location.href = `${import.meta.env.VITE_API_BASE || ""}/athlete/profile/strava/start`;
  };

  const ageText = status?.age_seconds != null ? fmtAge(status.age_seconds, t) : null;

  return (
    <Section
      title="Strava"
      ok={linked}
      hint={
        linked
          ? status?.strava_athlete_id
            ? `id: ${status.strava_athlete_id}`
            : t("status_connected")
          : available
            ? t("strava.hint_linked")
            : t("strava.coming_soon")
      }
    >
      {!linked && available && <PrimaryButton onClick={onConnect}>{t("strava.btn_connect")}</PrimaryButton>}
      {!linked && !available && <SecondaryButton disabled>{t("strava.btn_soon")}</SecondaryButton>}
      {linked && (
        <div className="space-y-2">
          <SecondaryButton
            onClick={() => syncNow.mutate()}
            disabled={syncNow.isPending}
            className="flex items-center justify-center gap-2"
          >
            <ArrowsClockwise className={`w-4 h-4 ${syncNow.isPending ? "animate-spin" : ""}`} />
            {syncNow.isPending ? t("sync.syncing") : t("sync.btn_sync")}
          </SecondaryButton>
          {ageText && !syncNow.isPending && (
            <div className="text-[11px] text-fg-muted text-center">
              {t("sync.last_sync", { time: ageText })}
            </div>
          )}
          {syncNow.isSuccess && syncNow.data?.ok && (
            <div className="text-[11px] text-ok text-center flex items-center justify-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {t("sync.done")}
              {syncNow.data.stats && (
                <span className="text-fg-muted">
                  ·{" "}
                  {[
                    (syncNow.data.stats.matched || 0) + (syncNow.data.stats.created || 0)
                      ? t("sync.workouts", { count: (syncNow.data.stats.matched || 0) + (syncNow.data.stats.created || 0) })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(", ") || t("sync.no_changes")}
                </span>
              )}
            </div>
          )}
          {syncNow.isError && (
            <div className="text-[11px] text-bad text-center">{t("sync.sync_error")}</div>
          )}
          {syncNow.isSuccess && !syncNow.data?.ok && (
            <div className="text-[11px] text-bad text-center">
              {syncNow.data?.error || tc("error")}
            </div>
          )}
          <button
            onClick={() => {
              if (confirm(t("strava.disconnect_confirm"))) {
                disconnect.mutate();
              }
            }}
            disabled={disconnect.isPending}
            className="w-full text-[11px] text-fg-muted hover:text-bad transition-colors"
          >
            {disconnect.isPending ? "..." : t("strava.btn_disconnect")}
          </button>
        </div>
      )}
    </Section>
  );
}


function ConnectBanner() {
  const { t } = useTranslation("profile");
  const [show, setShow] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("intervals") === "ok") {
      setShow(true);
      window.history.replaceState({}, "", window.location.pathname);
      const timer = setTimeout(() => setShow(false), 5000);
      return () => clearTimeout(timer);
    }
  }, []);
  if (!show) return null;
  return (
    <div className="rounded-card bg-ok/10 p-3 text-sm text-ok inline-flex items-center gap-2">
      <CheckCircle className="w-4 h-4" />
      <span>{t("intervals.connected_banner")}</span>
    </div>
  );
}

function AccountCard({ profile }: { profile: ProfileT }) {
  const nav = useNavigate();
  const { t } = useTranslation("profile");
  const initial = (profile.first_name?.[0] || profile.email?.[0] || "А").toUpperCase();
  const display = profile.first_name || profile.email || `id${profile.user_id}`;

  return (
    <Card variant="indigo" padding={16}>
      {/* Canon section ScreenProfile hero card (P3.dc.html:4218-4225) — avatar
          + name (17px bold) + bio eyebrow + location eyebrow + chevron.
          The status pill + email move into mono eyebrow form so the
          name gets the canon 17px hero weight. */}
      <div className="flex items-center gap-3">
        <AvatarUpload
          currentUrl={profile.avatar_url ?? null}
          size={56}
          editable={true}
          withRing={true}
          fallbackInitial={initial}
        />
        <div className="min-w-0 flex-1 ml-2">
          <div className="text-[17px] font-bold text-fg-primary truncate font-display leading-tight">
            {display}
          </div>
          {profile.email && profile.first_name && (
            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-fg-muted mt-1 truncate">
              {profile.email}
            </div>
          )}
          {/* Design section ScreenProfile — sport badge + mode chip */}
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            <span
              className="text-[9px] uppercase tracking-[0.20em] font-bold px-2 py-0.5 rounded-[4px]"
              style={{ background: "rgb(var(--accent-rgb) / 0.15)", color: "var(--indigo-300)", border: "1px solid rgb(var(--indigo-300-rgb) / 0.20)" }}
            >
              {profile.has_coach ? t("mode_coached", { defaultValue: "COACHED" }) : t("mode_solo", { defaultValue: "SOLO" })}
            </span>
            {profile.primary_sport && (
              <span className="text-[9px] uppercase tracking-[0.20em] font-bold text-fg-muted">
                {t(`sport.${profile.primary_sport}`, { defaultValue: profile.primary_sport })}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => { saveScrollPosition("scroll:profile"); nav("/settings"); }}
          aria-label={t("settings")}
          className="shrink-0 w-10 h-10 rounded-full bg-bg-raised border border-bg-border hover:border-accent/40 hover:bg-bg-base flex items-center justify-center transition-colors"
        >
          <SettingsIcon className="w-5 h-5 text-fg-secondary" />
        </button>
      </div>
    </Card>
  );
}

export function QuestionnaireCard() {
  const { t } = useTranslation("profile");
  const nav = useNavigate();
  const { data: schema } = useQuestionnaireSchema();
  const { data: ans } = useQuestionnaireAnswers();

  let filled = 0,
    total = 0;
  if (schema) {
    for (const sec of schema.sections) {
      for (const q of sec.questions) {
        total++;
        const v = ans?.answers?.[q.id];
        if (v != null && v !== "" && !(Array.isArray(v) && v.length === 0)) filled++;
      }
    }
  }
  const done = total > 0 && filled === total;
  const pct = total > 0 ? Math.round((filled / total) * 100) : 0;

  return (
    <button
      onClick={() => { saveScrollPosition("scroll:profile"); nav("/questionnaire"); }}
      className="w-full text-left indigo-card hover:bg-bg-raised p-4 transition-colors"
    >
      <div className="flex items-center gap-2">
        <div className="text-sm font-semibold text-fg-primary">{t("nav.questionnaire")}</div>
        <div className="ml-auto">
          <StatusPill ok={done} label={total > 0 ? `${filled} / ${total}` : "—"} />
        </div>
      </div>
      <div className="text-xs text-fg-secondary mt-2 leading-relaxed">
        {done ? t("questionnaire.hint_done") : t("questionnaire.hint_empty")}
      </div>
      {total > 0 && !done && (
        <div className="mt-3 h-1.5 rounded-full bg-bg-divider overflow-hidden">
          <div
            className="h-full bg-accent transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </button>
  );
}

/**
 * Phase 1 v149 — last unmapped drill registry exposure (30/30).
 * training_age methodology card — glossary today, year input UI later
 * when /api/profile gets a training_start_date field.
 */
function TrainingAgeCard() {
  const { t } = useTranslation("profile");
  const [openDrill, setOpenDrill] = useState(false);
  return (
    <Card variant="indigo" padding={16} data-testid="profile-training-age-card">
      <div className="flex items-center gap-2">
        <div className="text-sm font-semibold text-fg-primary">
          {t("training_age.title", { defaultValue: "Стаж в дисциплине" })}
        </div>
        {/* W-REBUILD-5: методология-выноска «ⓘ TRAINING AGE · методология»
            → «?» (tap-to-reveal, канон: методология за «?»). */}
        <button
          type="button"
          onClick={() => setOpenDrill(true)}
          data-testid="profile-training-age-drill"
          aria-label={t("training_age.title", { defaultValue: "Стаж в дисциплине" })}
          className="w-5 h-5 rounded-full bg-bg-raised border border-bg-border text-fg-muted hover:text-fg-primary text-[11px] leading-none flex items-center justify-center transition-colors"
        >
          ?
        </button>
      </div>
      {/* Карточка была заголовком с «?» и ничем больше — висящий хвост
          внизу профиля (v212). Пока поля «год начала» нет, честная строка
          объясняет, что это и когда появится. */}
      <div className="text-xs text-fg-secondary mt-1.5 leading-relaxed">
        {t("training_age.stub_hint", {
          defaultValue:
            "Сколько лет ты в спорте — влияет на то, как быстро растёт твоя форма. Поле появится в анкете; методология — за «?».",
        })}
      </div>
      <MetricDetailDrawer
        metricName={openDrill ? "training_age" : null}
        isOpen={openDrill}
        onClose={() => setOpenDrill(false)}
      />
    </Card>
  );
}

/**
 * Canon раздел ScreenProfile section 3 — СЕЗОН N ИТОГО card. 4-col grid:
 * swim / bike / run / hours + footer: «С января. Стартов в сезоне:
 * {races} ({prs} PR)». Source: useSeasonStats() (introduced v153 P3).
 *
 * Units-aware via useUnitsPreference (km / mi). Footer hides race count
 * when zero (canon: «отсюда стартует ваш сезон»). При isLoading
 * рендерит skeleton row (4 — — — — placeholders).
 */
// Phase 4 (design-recompose) — CTL/ATL/TSB tile row under the hero.
// Reuses PmcMini as-is (same component TodayPage renders) — no new
// visualization needed, just a new placement.
export function ProfilePmcMiniCard() {
  const { data: pmcSeries } = usePmcSeries();
  return (
    <PmcMini
      ctl={pmcSeries?.ctl ?? []}
      atl={pmcSeries?.atl ?? []}
      deltaCtl={pmcSeries?.delta_ctl_7d ?? null}
      deltaAtl={pmcSeries?.delta_atl_7d ?? null}
    />
  );
}

// Phase 4 (design-recompose) — PR grid (5K/10K/Half/Marathon + dates)
// under the hero. PRTiles already existed (built for Settings) but was
// never surfaced on Profile itself.
export function ProfilePrTilesCard() {
  const { data } = usePersonalRecords();
  return <PRTiles records={data ?? []} />;
}

function ProfileSeasonTotalsCard() {
  const { t } = useTranslation("profile");
  const { unit } = useUnitsPreference();
  const { data, isLoading } = useSeasonStats();

  const year = data?.year ?? new Date().getFullYear();
  const yearShort = String(year).slice(-2);

  const fmt = (km: number) => {
    const formatted = formatDistance(km, unit, km < 10 ? 1 : 0);
    return `${formatted.replace(/\.0+$/, "")}${unit === "mi" ? unitLabel("mi") : t("season.unit_km", { defaultValue: "км" })}`;
  };

  const cells = [
    {
      key: "swim",
      label: t("season.swim", { defaultValue: "Плав" }),
      value: data ? fmt(data.swim_km) : "—",
    },
    {
      key: "bike",
      label: t("season.bike", { defaultValue: "Вело" }),
      value: data ? fmt(data.bike_km) : "—",
    },
    {
      key: "run",
      label: t("season.run", { defaultValue: "Бег" }),
      value: data ? fmt(data.run_km) : "—",
    },
    {
      key: "hours",
      label: t("season.hours", { defaultValue: "Часов" }),
      value: data
        ? `${Math.round(data.total_hours)}${t("season.unit_hours_short", { defaultValue: "ч" })}`
        : "—",
    },
  ];

  return (
    <Card
      variant="indigo"
      padding={16}
      data-testid="profile-season-totals-card"
      className="space-y-3"
      aria-busy={isLoading}
    >
      <div className="flex items-center gap-2">
        <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
          {t("season.eyebrow", { defaultValue: "СЕЗОН {{n}} · ИТОГО", n: yearShort })}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {cells.map((c) => (
          <div key={c.key} data-testid={`profile-season-${c.key}`} className="space-y-0.5">
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-fg-muted">
              {c.label}
            </div>
            <div className="text-base font-semibold text-fg-primary num-display tabular-nums">
              {isLoading && !data ? <Skeleton className="h-5 w-14" /> : c.value}
            </div>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-fg-secondary leading-relaxed">
        {data && data.races > 0
          ? t("season.footer_with_races", {
              defaultValue:
                "С января. Стартов в сезоне: {{races}} ({{prs}} PR)",
              races: data.races,
              prs: data.prs,
            })
          : t("season.footer_no_races", {
              defaultValue: "С января. Накапливается.",
            })}
      </div>
    </Card>
  );
}

/**
 * Multi-year career peaks — personal bests of CTL / VO2max / CP / CSS across
 * seasons (2026-07-15). Complements the single-year СЕЗОН ИТОГО card: an elite
 * athlete wants to see peaks over a career, not just the current window.
 * Source: useCareerPeaks() → GET /athlete/analytics/career-peaks.
 */
function ProfileCareerPeaksCard() {
  const { data, isLoading } = useCareerPeaks();
  return (
    <CareerPeaksCard
      years={data?.years ?? []}
      alltimePeakCtl={data?.alltime_peak_ctl}
      alltimePeakCtlDate={data?.alltime_peak_ctl_date}
      alltimeBestVo2max={data?.alltime_best_vo2max}
      alltimeBestPowerWatts={data?.alltime_best_power_watts}
      alltimeBestCssSecPer100m={data?.alltime_best_css_sec_per_100m}
      loading={isLoading}
    />
  );
}

/**
 * Canon раздел ScreenProfile section 6 — УСТРОЙСТВА inline preview. List
 * connected data sources (intervals.icu + Strava) с connection state.
 * До этого статус подключений жил только в одном Section внизу страницы
 * (intervals/strava blocks), требуя scroll до низа для быстрой проверки.
 *
 * Renders compact card; tap → /settings/sources (or scrolls to existing
 * connect blocks при отсутствии deep-link).
 */
type DeviceRowState = "connected" | "disconnected" | "pending";

function ProfileDevicesCard({ profile }: { profile: ProfileT }) {
  const { t } = useTranslation("profile");
  const nav = useNavigate();
  const { data: stravaStatus } = useStravaStatus(true);

  // Подключение ≠ только атлетский OAuth: у coached-атлетов синк живёт на
  // тренерском ключе, и токена у атлета нет, а данные текут. Пара с
  // intervals (athlete_id) — и есть признак живого источника; «ОТКЛЮЧЕНО»
  // рядом с ежедневным импортом врало.
  const intervalsState: DeviceRowState =
    profile.has_intervals_token || profile.intervals_athlete_id
      ? "connected"
      : "disconnected";
  const stravaState: DeviceRowState =
    stravaStatus?.connected || profile.has_strava_token
      ? "connected"
      : "disconnected";

  const rows: Array<{
    key: string;
    name: string;
    detail: string | null;
    state: DeviceRowState;
  }> = [
    {
      key: "intervals",
      name: "intervals.icu",
      detail: profile.intervals_athlete_id ?? null,
      state: intervalsState,
    },
    {
      key: "strava",
      name: "Strava",
      detail:
        profile.strava_athlete_id != null
          ? `#${profile.strava_athlete_id}`
          : null,
      state: stravaState,
    },
  ];

  return (
    <button
      type="button"
      data-testid="profile-devices-card"
      onClick={() => {
        saveScrollPosition("scroll:profile");
        nav("/settings");
      }}
      className="w-full text-left indigo-card hover:bg-bg-raised p-4 transition-colors space-y-2.5"
    >
      <div className="flex items-center gap-2">
        <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
          {t("devices_card.eyebrow", { defaultValue: "УСТРОЙСТВА" })}
        </div>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-fg-muted">
          {t("devices_card.manage", { defaultValue: "Настроить →" })}
        </span>
      </div>
      <ul className="divide-y divide-bg-divider/60 -mx-1">
        {rows.map((r) => (
          <li
            key={r.key}
            data-testid={`profile-device-${r.key}`}
            className="flex items-center gap-3 px-1 py-2"
          >
            <span className="text-sm font-medium text-fg-primary flex-1 min-w-0 truncate">
              {r.name}
              {r.detail && (
                <span className="text-[10px] font-mono text-fg-muted ml-2 num-display">
                  {r.detail}
                </span>
              )}
            </span>
            <DeviceStatePill state={r.state} t={t} />
          </li>
        ))}
      </ul>
    </button>
  );
}

function DeviceStatePill({
  state,
  t,
}: {
  state: DeviceRowState;
  t: ReturnType<typeof useTranslation<"profile">>["t"];
}) {
  const label =
    state === "connected"
      ? t("devices_card.state_connected", { defaultValue: "ПОДКЛЮЧЕНО" })
      : state === "pending"
        ? t("devices_card.state_pending", { defaultValue: "ОЖИДАНИЕ" })
        : t("devices_card.state_disconnected", { defaultValue: "ОТКЛЮЧЕНО" });
  const tone =
    state === "connected"
      // «Подключено» — статус, значит --ok (okabe), а не лайм --active-500:
      // канон-сессия 24.07 вывела лайм из роли «хорошо», оставив его за
      // disc-run. Здесь он прятался за несуществующим именем
      // var(--status-success, …), из-за чего мимо сверок и прошёл.
      ? { fg: "var(--ok)", bg: "color-mix(in srgb, var(--ok) 10%, transparent)" }
      : state === "pending"
        ? { fg: "var(--warning-500)", bg: "color-mix(in srgb, var(--warning-500) 10%, transparent)" }
        : { fg: "var(--fg-muted)", bg: "color-mix(in srgb, var(--fg-muted) 8%, transparent)" };
  return (
    <span
      className="text-[10px] font-mono font-bold uppercase tracking-[0.16em] px-2 py-0.5 rounded-pill shrink-0"
      style={{ color: tone.fg, background: tone.bg }}
      data-testid={`profile-device-state-${state}`}
    >
      {label}
    </span>
  );
}

/**
 * Canon раздел ScreenProfile section 5 — ТРЕНЕР card. Avatar initial + name +
 * status chip (доступен / недоступен до {date}). Renders only when
 * profile.has_coach — для solo athletes section отсутствует (canon).
 * Tappable → /chat для DM (existing route).
 */
function ProfileCoachCard({ profile }: { profile: ProfileT }) {
  const { t } = useTranslation("profile");
  const nav = useNavigate();
  if (!profile.has_coach || !profile.coach) return null;
  const coach = profile.coach;
  const initial = (coach.name || "?").charAt(0).toUpperCase();
  const away = !!coach.is_currently_away;
  const awayUntil = coach.away_until ?? null;

  return (
    <button
      type="button"
      data-testid="profile-coach-card"
      onClick={() => {
        saveScrollPosition("scroll:profile");
        nav("/chat");
      }}
      className="w-full text-left indigo-card hover:bg-bg-raised p-4 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div
          aria-hidden
          className="w-11 h-11 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center shrink-0 text-fg-primary text-base font-bold num-display"
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
            {t("coach_card.eyebrow", { defaultValue: "ТРЕНЕР" })}
          </div>
          <div className="text-base font-semibold text-fg-primary truncate mt-0.5">
            {coach.name}
          </div>
          <div className="text-[11px] text-fg-secondary mt-0.5">
            {away
              ? awayUntil
                ? t("coach_card.away_until", {
                    defaultValue: "Недоступен до {{date}}",
                    date: awayUntil,
                  })
                : t("coach_card.away", { defaultValue: "Недоступен" })
              : t("coach_card.available", { defaultValue: "Открыт для запросов" })}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-fg-muted shrink-0">
          {t("coach_card.cta", { defaultValue: "Чат →" })}
        </span>
      </div>
    </button>
  );
}

function fmtPaceMmSs(sec: number | null | undefined): string {
  if (sec == null || sec <= 0) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Canon раздел ScreenProfile section 4 — ПОРОГИ inline preview. Replaces
 * ZonesLinkCard (single drill button) с 4-row table-like card showing
 * actual threshold values + sub-band labels («Z4 — 263–280»). Card
 * остаётся tappable → /zones для деталей.
 *
 * Derivation per row:
 *   • FTP — bike power Z4 min (LT2 watts approximation)
 *   • CSS — swim pace Z4 max (sec/100m)
 *   • Темп Z2 — run pace Z2 max (sec/km)
 *   • ЧСС max — run HR Z5 max
 */
function ProfileThresholdsCard() {
  const { t } = useTranslation("profile");
  const nav = useNavigate();
  const { data: zones, isLoading } = useZones();

  const findZone = (sport: string, type: string, num: number) =>
    zones?.find(
      (z) => z.sport === sport && z.zone_type === type && z.zone_number === num,
    );

  const ftpZ4 = findZone("ride", "power", 4) ?? findZone("bike", "power", 4);
  const cssZ4 = findZone("swim", "pace", 4);
  const runZ2 = findZone("run", "pace", 2);
  const hrZ5 = findZone("run", "hr", 5);

  const rows = [
    ftpZ4 && {
      key: "ftp",
      label: t("thresholds.ftp", { defaultValue: "FTP" }),
      value: `${Math.round(ftpZ4.min_value)} ${t("thresholds.unit_watt", { defaultValue: "Вт" })}`,
      band: `Z4 — ${Math.round(ftpZ4.min_value)}–${Math.round(ftpZ4.max_value)}`,
    },
    cssZ4 && {
      key: "css",
      label: t("thresholds.css", { defaultValue: "CSS" }),
      value: `${fmtPaceMmSs(cssZ4.max_value)} ${t("thresholds.per_100m", { defaultValue: "/100м" })}`,
      band: `Z4 — ${fmtPaceMmSs(cssZ4.min_value)}–${fmtPaceMmSs(cssZ4.max_value)}`,
    },
    runZ2 && {
      key: "tempo",
      label: t("thresholds.tempo_z2", { defaultValue: "Темп Z2" }),
      value: `${fmtPaceMmSs(runZ2.max_value)} ${t("thresholds.per_km", { defaultValue: "/км" })}`,
      band: `Z2 — ${fmtPaceMmSs(runZ2.min_value)}–${fmtPaceMmSs(runZ2.max_value)}`,
    },
    hrZ5 && {
      key: "hr_max",
      label: t("thresholds.hr_max", { defaultValue: "ЧСС max" }),
      value: `${Math.round(hrZ5.max_value)} ${t("thresholds.unit_bpm", { defaultValue: "уд" })}`,
      band: `Z5 — ${Math.round(hrZ5.min_value)}–${Math.round(hrZ5.max_value)}`,
    },
  ].filter(Boolean) as Array<{ key: string; label: string; value: string; band: string }>;

  return (
    <button
      type="button"
      onClick={() => {
        saveScrollPosition("scroll:profile");
        nav("/zones");
      }}
      data-testid="profile-thresholds-card"
      className="w-full text-left indigo-card hover:bg-bg-raised p-4 transition-colors space-y-2.5"
    >
      <div className="flex items-center gap-2">
        <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
          {t("thresholds.eyebrow", { defaultValue: "ПОРОГИ" })}
        </div>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-fg-muted">
          {t("thresholds.view_all", { defaultValue: "Все зоны →" })}
        </span>
      </div>
      {/* Пока идёт загрузка — скелетон, а не ложное «Зоны не настроены». */}
      {isLoading && rows.length === 0 && (
        <ul className="grid grid-cols-2 gap-x-3 gap-y-2" aria-hidden>
          {Array.from({ length: 4 }).map((_, i) => (
            <li key={i} className="flex flex-col gap-1">
              <Skeleton className="h-2.5 w-10" />
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-2.5 w-20" />
            </li>
          ))}
        </ul>
      )}
      {!isLoading && rows.length === 0 && (
        <div className="text-xs text-fg-secondary leading-relaxed">
          {t("thresholds.empty", {
            defaultValue: "Зоны не настроены. Откройте, чтобы задать.",
          })}
        </div>
      )}
      {rows.length > 0 && (
        <ul className="grid grid-cols-2 gap-x-3 gap-y-2">
          {rows.map((r) => (
            <li
              key={r.key}
              data-testid={`profile-threshold-${r.key}`}
              className="flex flex-col gap-0.5"
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-fg-muted">
                {r.label}
              </div>
              <div className="text-sm font-semibold text-fg-primary num-display">
                {r.value}
              </div>
              <div className="text-[10px] font-mono text-fg-muted num-display">
                {r.band}
              </div>
            </li>
          ))}
        </ul>
      )}
    </button>
  );
}

/** Sport-profile shortcuts — opens the per-discipline dashboard. */
export function SportProfileLinkRow({ disciplines }: { disciplines: string[] }) {
  const nav = useNavigate();
  const { t } = useTranslation("profile");
  const { t: tc } = useTranslation("common");
  // Map raw discipline values to the canonical four destinations.
  const SHORTCUT_MAP: Record<string, "run" | "bike" | "swim" | "triathlon"> = {
    run: "run",
    running: "run",
    trail: "run",
    bike: "bike",
    ride: "bike",
    cycling: "bike",
    gravel: "bike",
    swim: "swim",
    swimming: "swim",
    triathlon: "triathlon",
    tri: "triathlon",
  };
  const targets = Array.from(
    new Set(disciplines.map((d) => SHORTCUT_MAP[d.toLowerCase()]).filter(Boolean)),
  ) as Array<"run" | "bike" | "swim" | "triathlon">;
  if (targets.length === 0) return null;
  return (
    <Card variant="indigo" padding={12} className="space-y-2" data-testid="profile-sport-profile-row">
      <div className="label-caps text-fg-muted px-1">{t("sport_profile_row", { defaultValue: "Спорт-профиль" })}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {targets.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => {
              saveScrollPosition("scroll:profile");
              nav(`/sport-profile/${d}`);
            }}
            className="px-3 py-2.5 rounded-pill bg-bg-raised text-fg-primary text-sm font-semibold hover:bg-accent/10 hover:text-accent transition-colors capitalize"
            data-testid={`profile-sport-profile-link-${d}`}
          >
            {tc(`sport.${d}`)}
          </button>
        ))}
      </div>
    </Card>
  );
}

// Спорт-профиль показывает только дисциплины атлета (по primary_sport),
// а не хардкод всех четырёх. Триатлон раскрывается на составляющие,
// одиночные виды — на себя; hyrox/strength не имеют sport-profile страницы
// и дают пустой список (строка скрывается через guard в компоненте).
export function sportProfileDisciplines(primarySport: string | null | undefined): string[] {
  const map: Record<string, string[]> = {
    run: ["run"],
    trail: ["run"],
    ride: ["bike"],
    gravel: ["bike"],
    swim: ["swim"],
    triathlon: ["swim", "bike", "run", "triathlon"],
    duathlon: ["run", "bike"],
  };
  const key = (primarySport ?? "").toLowerCase();
  return map[key] ?? (key ? [key] : []);
}

export function ProfilePage() {
  const { data: profile, isLoading, isError, refetch, isFetching } = useProfile();
  const { t } = useTranslation("profile");

  useScrollRestore("scroll:profile");

  if (isLoading) {
    return <div className="p-4 text-fg-muted text-sm">{t("loading", { ns: "common" })}</div>;
  }

  // Раньше при сбое загрузки профиля `!profile` держал экран в вечной
  // «Загрузка…». Теперь — явная ошибка с повтором (не белый экран).
  if (isError || !profile) {
    return (
      <div className="p-6 flex flex-col items-center gap-3 text-center" data-testid="profile-load-error">
        <div className="text-fg-muted text-sm">
          {t("load_error", { defaultValue: "Не удалось загрузить профиль" })}
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="px-4 py-1.5 rounded-pill ring-1 ring-accent/40 bg-accent/10 text-accent text-[12px] font-medium disabled:opacity-50"
        >
          {isFetching
            ? t("loading", { ns: "common" })
            : t("retry", { ns: "common", defaultValue: "Повторить" })}
        </button>
      </div>
    );
  }
  return (
    <div className="p-3 space-y-3">
      <ConnectBanner />
      <AccountCard profile={profile} />

      {/* W-REBUILD-6 (мандат раздел 6.4 «ноль дублей»): PMC-карта уехала —
          форма живёт в Анализе (glance + PMC), Профиль = identity.
          Под hero остаются личные рекорды. */}
      <div data-testid="profile-pmc-pr-row">
        <ProfilePrTilesCard />
      </div>

      {/* Canon раздел ScreenProfile — СЕЗОН ИТОГО + ПОРОГИ + ТРЕНЕР + УСТРОЙСТВА
          inline section. Промоутит aggregate stats + actual thresholds +
          coach summary + connected sources над «Прогресс» drill list. */}
      <div className="space-y-2" data-testid="profile-canon-section">
        <div className="text-[10px] uppercase tracking-wider text-fg-muted px-1">
          {t("section_profile", { defaultValue: "Профиль" })}
        </div>
        <ProfileSeasonTotalsCard />
        <ProfileCareerPeaksCard />
        <ProfileThresholdsCard />
        <ProfileCoachCard profile={profile} />
        <ProfileDevicesCard profile={profile} />
      </div>

      {/* Спорт-профили — данные-hub, чипы по реальным дисциплинам атлета */}
      <SportProfileLinkRow disciplines={sportProfileDisciplines(profile.primary_sport)} />

      {/* W-REBUILD-4 (мандат раздел 6.4): 4 секции гигантских link-карточек
          (~1500px: Самочувствие/Прогресс/Снаряжение/Аккаунт) → один
          компакт-грид «Разделы». Данные — сверху, ссылки — подвалом. */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-fg-muted px-1">
          {t("links.section", { defaultValue: "Разделы" })}
        </div>
        <ProfileLinksGrid />
      </div>

      {/* INDIGO Workstation settings — Reader/Companion readout */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-fg-muted px-1">Workstation</div>
        <Card variant="indigo" padding={16}>
          <CompanionModeToggle />
        </Card>
      </div>

      {/* Стаж — методология за drill, компакт в самом низу */}
      <TrainingAgeCard />
    </div>
  );
}
